// Chi tiết một bảng hoa hồng: mức theo từng phiên bản (đang áp / hẹn tháng sau / bị bản sau
// thay), CTV đang dùng, sửa, lưu trữ (bảng đang có CTV dùng thì phải gán bảng khác trước).

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Archive, Edit2 } from 'lucide-react'
import { toast } from 'sonner'
import { post } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useUnitCan } from '@/lib/unitAccess'
import { Button } from '@/components/ui/button'
import { confirm } from '@/components/ui/confirm'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { errorDetails, errorMessage } from '@/components/price-lists/types'
import { TierTable } from './parts'
import { MODE_LABEL, MODE_SHORT, monthLabel, scheduledVersion, supersededBy, versionAt, type Scheme } from './types'

export function SchemeDetailDialog({
  scheme,
  month,
  usingNow,
  usingNext,
  ctvName,
  onClose,
  onEdit,
}: {
  scheme: Scheme
  /** Tháng hiện tại (YYYY-MM). */
  month: string
  /** CTV đang dùng bảng tháng này / sẽ chuyển sang bảng từ tháng sau. */
  usingNow: string[]
  usingNext: string[]
  ctvName: (id: string) => string
  onClose: () => void
  onEdit: () => void
}) {
  const qc = useQueryClient()
  const current = versionAt(scheme, month)
  const scheduled = scheduledVersion(scheme, month)
  const [picked, setPicked] = useState<number | null>(null)
  const [blockedBy, setBlockedBy] = useState<string[] | null>(null)
  const shownNo = picked ?? current?.version ?? scheme.versions[0]?.version ?? null
  const shown = scheme.versions.find((v) => v.version === shownNo)
  const archived = !!scheme.archivedAt
  // Nhân viên chỉ xem bảng; sửa / lưu trữ cần quyền "Bảng giá & hoa hồng cấp dưới" (docs/KE_HOACH_THANH_VIEN_DON_VI.md)
  const canPricing = useUnitCan().can('network.pricing')

  const archive = useMutation({
    mutationFn: () => post(`/commissions/schemes/${scheme.id}/archive`),
    onSuccess: () => {
      toast.success(`Đã lưu trữ bảng ${scheme.code}`)
      qc.invalidateQueries({ queryKey: ['commissions'] })
      onClose()
    },
    onError: (e) => {
      const ids = errorDetails<{ ctvShopIds?: string[] }>(e, 'commission.in_use')?.ctvShopIds
      if (ids) setBlockedBy(ids)
      else toast.error(errorMessage(e, 'Không lưu trữ được bảng chiết khấu'))
    },
  })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader className="pr-8">
          <DialogTitle>{scheme.name}</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{scheme.code}</span> · {MODE_LABEL[current?.mode ?? scheme.mode]} · {usingNow.length} CTV đang dùng
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {archived ? (
            <p className="rounded-lg bg-paperSoft px-3 py-2 text-sm text-inkSoft">Bảng đã lưu trữ.</p>
          ) : !canPricing ? null : (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="gap-1.5" onClick={onEdit}>
                <Edit2 className="size-4" /> Sửa bảng
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 text-danger hover:bg-danger/10"
                disabled={archive.isPending}
                onClick={async () => {
                  const ok = await confirm({
                    title: `Lưu trữ bảng ${scheme.code}?`,
                    description: 'Bảng đã lưu trữ không gán và không sửa được nữa.',
                    confirmLabel: 'Lưu trữ',
                    tone: 'danger',
                  })
                  if (ok) archive.mutate()
                }}
              >
                <Archive className="size-4" /> Lưu trữ
              </Button>
            </div>
          )}

          {blockedBy && (
            <div className="rounded-lg bg-danger/5 p-3 text-sm text-danger">
              <p className="font-medium">Chưa lưu trữ được: {blockedBy.length} CTV đang dùng bảng này.</p>
              <p className="mt-1 text-xs">{blockedBy.map(ctvName).join(', ')}</p>
            </div>
          )}

          {scheduled && (
            <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">
              Từ tháng <strong>{monthLabel(scheduled.effectiveFromMonth)}</strong> áp dụng phiên bản v{scheduled.version} ({MODE_SHORT[scheduled.mode]}). Tháng{' '}
              {monthLabel(month)} giữ {current ? `v${current.version}` : 'mức cũ'}.
            </p>
          )}

          <section className="space-y-2">
            <h3 className="text-sm font-medium text-ink">
              {shown
                ? shown.version === current?.version
                  ? `Mức đang áp dụng · v${shown.version}`
                  : `Mức phiên bản v${shown.version} · từ tháng ${monthLabel(shown.effectiveFromMonth)}`
                : 'Mức chiết khấu'}
            </h3>
            {shown && <TierTable tiers={shown.tiers} />}
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-medium text-ink">CTV đang dùng ({usingNow.length})</h3>
            {usingNow.length === 0 ? (
              <p className="text-sm text-muted">Chưa CTV nào dùng bảng này tháng {monthLabel(month)}.</p>
            ) : (
              <p className="text-sm text-ink">{usingNow.map(ctvName).join(', ')}</p>
            )}
            {usingNext.length > 0 && (
              <p className="text-xs text-inkSoft">
                Chuyển sang bảng này từ tháng sau: {usingNext.map(ctvName).join(', ')}
              </p>
            )}
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-medium text-ink">Lịch sử phiên bản</h3>
            <ul className="divide-y divide-rule rounded-lg border border-rule">
              {scheme.versions.map((v) => {
                const replacedBy = v.version === current?.version ? null : supersededBy(scheme, v)
                return (
                  <li key={v.version}>
                    <button
                      type="button"
                      onClick={() => setPicked(v.version)}
                      className={cn(
                        'motion-state flex min-h-11 w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-sm hover:bg-paperSoft',
                        v.version === shownNo && 'bg-accent/5',
                      )}
                    >
                      <span className="font-mono text-xs font-semibold text-ink">v{v.version}</span>
                      {v.version === current?.version && <span className="rounded-full bg-ok/15 px-2 py-0.5 text-xs font-medium text-ok">Đang áp dụng</span>}
                      {v.version === scheduled?.version && <span className="rounded-full bg-warn/15 px-2 py-0.5 text-xs font-medium text-warn">Từ tháng sau</span>}
                      {replacedBy && v.effectiveFromMonth > month && (
                        <span className="rounded-full bg-paperSoft px-2 py-0.5 text-xs font-medium text-inkSoft">Không áp dụng — thay bằng v{replacedBy.version}</span>
                      )}
                      <span className="min-w-0 flex-1 text-xs text-inkSoft">
                        Từ tháng {monthLabel(v.effectiveFromMonth)} · {MODE_SHORT[v.mode]} · {v.tiers.length} bậc
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
